import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Thumbs, Autoplay, Pagination, EffectFade } from 'swiper/modules';
import { Swiper as SwiperType } from 'swiper';
import { FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeading from '../ui/SectionHeading';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const galleryImages = [
  {
    src: '/images/gallery/Living Room.jpg',
    alt: 'Living Room',
    title: 'Spacious Living Room'
  },
  {
    src: '/images/gallery/Bedroom 1.jpg',
    alt: 'Bedroom 1',
    title: 'Comfortable Master Bedroom'
  },
  {
    src: '/images/gallery/Bedroom 2.jpg',
    alt: 'Bedroom 2',
    title: 'Second Bedroom'
  },
  {
    src: '/images/gallery/Bedroom 3.jpg',
    alt: 'Bedroom 3',
    title: 'Third Bedroom'
  },
  {
    src: '/images/gallery/kitchen.jpeg',
    alt: 'Kitchen',
    title: 'Fully Equipped Kitchen'
  },
  {
    src: '/images/gallery/kitchen2.jpeg',
    alt: 'Kitchen View',
    title: 'Kitchen Alternative View'
  },
  {
    src: '/images/gallery/Dining area.jpeg',
    alt: 'Dining Area',
    title: 'Elegant Dining Area'
  },
  {
    src: '/images/gallery/Full Bthroom 1.jpeg',
    alt: 'Bathroom 1',
    title: 'Modern Bathroom'
  },
  {
    src: '/images/gallery/Full Bathroom 2.jpg',
    alt: 'Bathroom 2',
    title: 'Second Bathroom'
  },
  {
    src: '/images/gallery/Exterior.jpg',
    alt: 'Exterior',
    title: 'Homestay Exterior'
  },
  {
    src: '/images/gallery/Garage.jpg',
    alt: 'Garage',
    title: 'Spacious Garage'
  },
  {
    src: '/images/gallery/Prayer Room.jpg',
    alt: 'Prayer Room',
    title: 'Prayer Room'
  },
  {
    src: '/images/gallery/Laundry Area.jpeg',
    alt: 'Laundry Area',
    title: 'Convenient Laundry Area'
  }
];

export default function ImageGallery() {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  
  // Navigation refs for custom buttons
  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  // Handle client-side mounting
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Open lightbox with specific image
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setShowLightbox(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when lightbox is open
  };

  // Close lightbox
  const closeLightbox = () => {
    setShowLightbox(false);
    document.body.style.overflow = ''; // Restore scrolling
  };

  return (
    <>
      <SectionHeading 
        title="Gallery" 
        subtitle="Take a peek inside Mikhael Homestay – spacious, modern, and fully equipped"
      />
      
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="max-w-full"
      >
        {isMounted && (
          <>
            {/* Main Swiper */}
            <div className="relative mb-6">
              <Swiper
                thumbs={{ swiper: thumbsSwiper }}
                modules={[Navigation, Thumbs, EffectFade, Pagination]}
                effect="fade"
                speed={800}
                slidesPerView={1}
                pagination={{ clickable: true, dynamicBullets: true }}
                className="main-swiper rounded-xl overflow-hidden shadow-lg"
                onInit={(swiper) => {
                  // @ts-ignore
                  swiper.params.navigation.prevEl = prevRef.current;
                  // @ts-ignore
                  swiper.params.navigation.nextEl = nextRef.current;
                  swiper.navigation.init();
                  swiper.navigation.update();
                }}
              >
                {galleryImages.map((image, index) => (
                  <SwiperSlide key={index} onClick={() => openLightbox(index)}>
                    <div className="relative w-full h-full cursor-pointer">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 md:p-8">
                        <h3 className="text-white text-xl md:text-2xl font-semibold">{image.title}</h3>
                      </div>
                      <div className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-all hover:scale-110">
                        <FaExpand size={16} />
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              
              {/* Custom navigation buttons */}
              <motion.div 
                ref={prevRef} 
                className="swiper-button-prev absolute left-4 top-1/2 z-10 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer shadow-soft transition-all"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaChevronLeft size={18} />
              </motion.div>
              <motion.div 
                ref={nextRef} 
                className="swiper-button-next absolute right-4 top-1/2 z-10 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 w-12 h-12 rounded-full flex items-center justify-center cursor-pointer shadow-soft transition-all"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaChevronRight size={18} />
              </motion.div>
            </div>
            
            {/* Thumbnails Swiper */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Swiper
                onSwiper={setThumbsSwiper}
                spaceBetween={12}
                slidesPerView={3}
                freeMode={true}
                watchSlidesProgress={true}
                modules={[FreeMode, Navigation, Thumbs]}
                className="thumbs-swiper"
                breakpoints={{
                  640: { slidesPerView: 4, spaceBetween: 16 },
                  768: { slidesPerView: 5, spaceBetween: 16 },
                  1024: { slidesPerView: 6, spaceBetween: 16 },
                }}
              >
                {galleryImages.map((image, index) => (
                  <SwiperSlide key={index} className="cursor-pointer">
                    <div className="relative h-24 rounded-lg overflow-hidden transition-all hover:opacity-100 opacity-80 hover:ring-2 hover:ring-blue-500 shadow-sm">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </motion.div>
          </>
        )}
      </motion.div>
      
      {/* Lightbox Modal */}
      <AnimatePresence>
        {showLightbox && (
          <motion.div 
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="relative w-full max-w-6xl h-[85vh] px-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.button 
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-all"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
              
              <Swiper
                initialSlide={lightboxIndex}
                spaceBetween={10}
                navigation={true}
                modules={[Navigation]}
                className="h-full"
              >
                {galleryImages.map((image, index) => (
                  <SwiperSlide key={index} className="flex items-center justify-center">
                    <div className="relative w-full h-full">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="absolute inset-0 w-full h-full object-contain"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 text-center">
                        <h3 className="text-white text-xl md:text-2xl font-semibold">{image.title}</h3>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Custom styles */}
      <style>
        {`
        .main-swiper {
          height: 500px;
        }
        
        .thumbs-swiper .swiper-slide-thumb-active > div {
          opacity: 1;
          ring-width: 2px;
          ring-color: rgb(59, 130, 246);
          transform: scale(1.05);
          transition: all 0.3s ease;
        }
        
        @media (max-width: 768px) {
          .main-swiper {
            height: 350px;
          }
        }
        
        /* Hide default navigation arrows */
        .swiper-button-next:after, .swiper-button-prev:after {
          display: none;
        }
        `}
      </style>
    </>
  );
}
